/**
 * BuyerInvoicesList — buyer-perspective invoice register.
 *
 * Mirror of `SellerInvoices.jsx` but inverted: every row is a bill the
 * buyer owes (or owed). Surfaced under Payments → Invoices in the side
 * nav so the buyer's mental model "invoices = money I owe / paid" lives
 * with their other financial records.
 *
 * Row actions:
 *   • Pay        — opens the existing BuyerPayments flow with this invoice
 *                  pre-selected (deep-links into /buyer/payments#credit)
 *   • View       — opens BuyerInvoiceDetailDialog (full breakdown + credit
 *                  memos timeline)
 *   • Download   — PDF (only for paid invoices that have a receipt)
 *
 * No money moves from this page — actions either deep-link to existing
 * flows or open read-only drawers. That's intentional: industry norm
 * (Mercury, Brex, Two.inc) keeps invoice-list pages read-mostly + send
 * the user to a dedicated pay screen for transactions.
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FileText, Search, Filter, CreditCard, Eye, Download, Loader2, ChevronLeft, ChevronRight, Repeat, Clock, AlertCircle, CheckSquare, Square } from "lucide-react";
import api, { money } from "../../lib/api";
import { PageHeader } from "../../components/PortalShell";
import { useSupplier } from "../../context/SupplierContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../../components/ui/select";
import { toast } from "sonner";
import BuyerInvoiceDetailDialog from "../../components/BuyerInvoiceDetailDialog";
import InvoiceAmountWithEvents from "../../components/InvoiceAmountWithEvents";
import PaymentsTabBar from "../../components/PaymentsTabBar";
import BulkPayDialog from "../../components/BulkPayDialog";


const STATUS_LABELS = {
  draft:           "Draft",
  sent:            "Awaiting payment",
  outstanding:     "Awaiting payment",
  paying:          "Paying",
  paid:            "Paid",
  credit_terms:    "On terms",
  overdue:         "Overdue",
  voided:          "Voided",
  cancelled:       "Cancelled",
  fully_refunded:  "Refunded",
};

function StatusChip({ status }) {
  const map = {
    paid:         "bg-accent/30 border border-accent/40",
    overdue:      "bg-destructive/10 text-destructive",
    credit_terms: "bg-sky-50 text-sky-700 border border-sky-200",
    voided:       "bg-foreground/10 text-foreground/55 border border-foreground/15",
    cancelled:    "bg-foreground/10 text-foreground/55 border border-foreground/15",
  };
  return (
    <span className={`chip ${map[status] || "bg-secondary"}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}


// Due-date cell — calendar date + urgency micro-badge (Due in N · N
// overdue · neutral when paid). Buyers think in dates, not "NET30"
// jargon, so this replaces the old Terms column.
function DueCell({ invoice }) {
  const dueRaw = invoice.due_date || invoice.due_at;
  if (!dueRaw) {
    // No due date yet → fall back to terms string + creation date so
    // the column never reads as blank.
    return (
      <div>
        <div className="uppercase text-[10.5px] mono tracking-wider text-foreground/55">
          {invoice.terms || "—"}
        </div>
        {invoice.created_at && (
          <div className="text-[10.5px] text-foreground/45 mt-0.5">
            Issued {new Date(invoice.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
          </div>
        )}
      </div>
    );
  }
  const due = new Date(dueRaw);
  const today = new Date();
  const diffMs = due.getTime() - today.getTime();
  const diffDays = Math.round(diffMs / 86400000);
  const fmt = due.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  const isPaid = invoice.status === "paid";
  let badge = null;
  if (isPaid) {
    badge = null;   // paid invoices don't need urgency
  } else if (diffDays < 0) {
    badge = (
      <span className="inline-flex items-center gap-1 mt-1 text-[10.5px] rounded px-1.5 py-0.5 bg-rose-50 text-rose-700 border border-rose-200">
        <AlertCircle className="w-2.5 h-2.5"/>{Math.abs(diffDays)}d overdue
      </span>
    );
  } else if (diffDays <= 3) {
    badge = (
      <span className="inline-flex items-center gap-1 mt-1 text-[10.5px] rounded px-1.5 py-0.5 bg-amber-50 text-amber-800 border border-amber-200">
        <Clock className="w-2.5 h-2.5"/>Due in {diffDays}d
      </span>
    );
  } else if (diffDays <= 30) {
    badge = (
      <span className="inline-flex items-center gap-1 mt-1 text-[10.5px] rounded px-1.5 py-0.5 bg-secondary text-foreground/60 border border-foreground/10">
        <Clock className="w-2.5 h-2.5"/>Due in {diffDays}d
      </span>
    );
  }
  return (
    <div>
      <div className="text-[13px]">{fmt}</div>
      {badge}
    </div>
  );
}


export default function BuyerInvoicesList() {
  const nav = useNavigate();
  const loc = useLocation();
  const { activeSellerName } = useSupplier() || {};
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [detailInvoice, setDetailInvoice] = useState(null);
  // Pagination — same rhyme as WalletActivityPanel / SellerInvoices.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
  // ── Bulk-pay state (Jun 2026) ─────────────────────────────────────
  // `selectedIds` is a Set of invoice ids the buyer has ticked. We
  // store as a Set for O(1) add/has/remove + auto-dedup. Cleared
  // whenever the visible filter changes (the user's mental model
  // breaks if a selection survives a filter pivot).
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [wallet, setWallet] = useState({ balance: 0 });
  useEffect(() => {
    api.get("/wallet").then(r => setWallet(r.data || { balance: 0 })).catch(() => {});
  }, []);
  // Light-touch cross-link: ?focus=<id> from the Activity tab's invoice
  // chip → scroll that row into view + flash highlight.
  const focusId = new URLSearchParams(loc.search).get("focus") || null;
  useEffect(() => {
    if (!focusId || items.length === 0) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-invoice-row="${focusId}"]`);
      if (el) el.scrollIntoView({ block: "center", behavior: "smooth" });
    }, 60);
    return () => clearTimeout(t);
  }, [focusId, items]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/invoices");
      setItems(Array.isArray(data) ? data : (data?.items || []));
    } catch { setItems([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return items.filter(i => {
      if (statusFilter !== "all" && i.status !== statusFilter) return false;
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (i.code || "").toLowerCase().includes(q)
          || (i.seller_name || "").toLowerCase().includes(q);
    });
  }, [items, search, statusFilter]);

  // Reset to page 1 whenever filters change.
  useEffect(() => { setPage(1); setSelectedIds(new Set()); }, [search, statusFilter]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage   = Math.min(page, totalPages);
  const pageStart  = (safePage - 1) * pageSize;
  const pageEnd    = pageStart + pageSize;
  const paged      = filtered.slice(pageStart, pageEnd);

  // ── Bulk-pay derived state ────────────────────────────────────────
  const isPayable = (i) => {
    const due = Math.max(0, Number(i.amount || 0) - Number(i.paid_amount || 0));
    return ["sent","outstanding","paying","credit_terms","overdue"].includes(i.status) && due > 0;
  };
  const payableOnPage = useMemo(() => paged.filter(isPayable), [paged]);
  const selectedInvoices = useMemo(
    () => filtered.filter(i => selectedIds.has(i.id) && isPayable(i)),
    [filtered, selectedIds],
  );
  const selectedTotal = selectedInvoices.reduce(
    (s, i) => s + Math.max(0, Number(i.amount || 0) - Number(i.paid_amount || 0)),
    0,
  );
  const allPageSelected = payableOnPage.length > 0
    && payableOnPage.every(i => selectedIds.has(i.id));
  const toggleRow = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAllOnPage = () => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allPageSelected) {
        payableOnPage.forEach(i => next.delete(i.id));
      } else {
        payableOnPage.forEach(i => next.add(i.id));
      }
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  // Headline stats — informational tiles at the top of the list.
  const totals = useMemo(() => {
    const outstanding = items
      .filter(i => ["sent", "outstanding", "paying", "credit_terms", "overdue"].includes(i.status))
      .reduce((s, i) => s + Number(i.amount || 0) - Number(i.paid_amount || 0), 0);
    const overdueCount = items.filter(i => i.status === "overdue").length;
    const paidThisMonth = items
      .filter(i => i.status === "paid" && i.paid_at && (Date.now() - new Date(i.paid_at).getTime()) < 30 * 86400000)
      .reduce((s, i) => s + Number(i.amount || 0), 0);
    return { outstanding, overdueCount, paidThisMonth };
  }, [items]);

  const goPay = (inv) => {
    // Deep-link into BuyerPayments and auto-open the pay drawer.
    // The page reads `?pay=<id>` (used everywhere else); keep both
    // hash + query in sync so older bookmarks still route correctly.
    nav(`/buyer/payments?pay=${inv.id}`);
  };

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (search.trim()) params.set("q", search.trim());
      const qs = params.toString();
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/invoices/export.csv${qs ? "?" + qs : ""}`;
      const token = localStorage.getItem("token");
      const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `verqo-invoices-${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error("Could not export CSV");
    }
  };

  return (
    <>
      <PageHeader
        title="Invoices"
        subtitle="Every bill on your account — paid, due, and on terms. Refunds via credit memo show inline on each invoice's detail."
      />

      {/* Sticky tab strip — same as on /buyer/payments. Lets the buyer
          flip back to Wallet / Credit / Activity without going via the
          sidebar, AND keeps the visual rhythm consistent across the hub. */}
      <PaymentsTabBar role="buyer"/>

      <div className="p-8 space-y-6" data-testid="buyer-invoices-list">
        {/* ── Stats strip ───────────────────────────────────────────── */}
        <div className="grid grid-cols-3 gap-px bg-border rounded-2xl overflow-hidden border border-border">
          <div className="bg-background p-5">
            <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/50">Outstanding</div>
            <div className="display text-3xl mt-1.5 tabular-nums" data-testid="bi-outstanding">
              {money(totals.outstanding)}
            </div>
            <div className="text-[11px] text-foreground/55 mt-0.5">
              {items.filter(i => ["sent","outstanding","paying","credit_terms","overdue"].includes(i.status)).length} unpaid invoice{items.length === 1 ? "" : "s"}
            </div>
          </div>
          <div className="bg-background p-5">
            <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/50">Overdue</div>
            <div className="display text-3xl mt-1.5 tabular-nums text-destructive" data-testid="bi-overdue">
              {totals.overdueCount}
            </div>
            <div className="text-[11px] text-foreground/55 mt-0.5">past their due date</div>
          </div>
          <div className="bg-background p-5">
            <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/50">Paid (30d)</div>
            <div className="display text-3xl mt-1.5 tabular-nums" data-testid="bi-paid-30d">
              {money(totals.paidThisMonth)}
            </div>
          </div>
        </div>

        {/* ── Filter bar ────────────────────────────────────────────── */}
        <div className="flex items-center gap-2.5">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 text-foreground/40 absolute left-3 top-1/2 -translate-y-1/2"/>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search invoice # or seller…"
              className="pl-9 h-10"
              data-testid="bi-search"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-10 w-44" data-testid="bi-status-filter">
              <Filter className="w-3.5 h-3.5 mr-1.5"/>
              <SelectValue/>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="sent">Awaiting payment</SelectItem>
              <SelectItem value="paying">Paying</SelectItem>
              <SelectItem value="credit_terms">On terms</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="voided">Voided</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" className="rounded-full h-10 ml-auto"
                  onClick={exportCsv}
                  data-testid="bi-export-csv">
            <Download className="w-3.5 h-3.5 mr-1.5"/>Export CSV
          </Button>
        </div>

        {/* ── Invoice table ─────────────────────────────────────────── */}
        <div className="rounded-2xl bg-white border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50">
              <tr className="text-left">
                <th className="px-3 py-3 w-10">
                  <button
                    type="button"
                    onClick={toggleAllOnPage}
                    disabled={payableOnPage.length === 0}
                    data-testid="bi-select-all"
                    className="inline-flex items-center text-foreground/65 hover:text-foreground transition-colors disabled:opacity-30"
                    title={allPageSelected ? "Clear page selection" : "Select all payable on this page"}
                  >
                    {allPageSelected
                      ? <CheckSquare className="w-4 h-4 text-foreground"/>
                      : <Square className="w-4 h-4"/>}
                  </button>
                </th>
                <th className="px-5 py-3 font-medium">Invoice</th>
                <th className="px-5 py-3 font-medium">Seller</th>
                <th className="px-5 py-3 font-medium">Due</th>
                <th className="px-5 py-3 font-medium">Amount</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3"/>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading && (
                <tr>
                  <td colSpan={7} className="px-5 py-12 text-center text-foreground/55">
                    <Loader2 className="w-4 h-4 animate-spin inline-block mr-2"/>Loading invoices…
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-5 py-12 text-center text-foreground/55" data-testid="bi-empty">
                    {activeSellerName
                      ? `No invoices from ${activeSellerName} yet. They'll show up here the moment ${activeSellerName} bills you.`
                      : "No invoices yet. They'll show up here once a seller bills you."}
                  </td>
                </tr>
              )}
              {!loading && paged.map(i => {
                const cmTotal = Number(i.credit_memo_total || 0);
                const refunded = Number(i.refunded_total || 0);
                const balance = Math.max(0, Number(i.amount || 0) - Number(i.paid_amount || 0));
                const canPay = isPayable(i);
                const isChecked = selectedIds.has(i.id);
                return (
                  <tr key={i.id} data-testid={`bi-${i.code}`}
                      data-invoice-row={i.id}
                      className={`${focusId === i.id ? "bg-accent/15 animate-pulse" : ""} ${isChecked ? "bg-accent/8" : ""}`}>
                    <td className="px-3 py-4 w-10">
                      {canPay ? (
                        <button
                          type="button"
                          onClick={() => toggleRow(i.id)}
                          data-testid={`bi-check-${i.code}`}
                          className="inline-flex items-center text-foreground/65 hover:text-foreground transition-colors"
                          title="Add to bulk payment"
                        >
                          {isChecked
                            ? <CheckSquare className="w-4 h-4 text-foreground"/>
                            : <Square className="w-4 h-4"/>}
                        </button>
                      ) : (
                        <span className="inline-block w-4 h-4"/>
                      )}
                    </td>
                    <td className="px-5 py-4 mono whitespace-nowrap">
                      <div className="inline-flex items-center gap-1.5">
                        {i.code}
                        {i.kind === "pre_credit_deposit" && (
                          <span data-testid={`bi-pcd-chip-${i.code}`}
                                className="inline-flex items-center gap-1 px-1.5 h-[18px] rounded text-[9.5px] mono uppercase tracking-wider bg-sky-100 text-sky-800 border border-sky-200 normal-case">
                            Pre-credit
                          </span>
                        )}
                      </div>
                      {i.order_code && (
                        <button
                          type="button"
                          onClick={() => nav(`/buyer/orders?focus=${i.order_id}#all`)}
                          data-testid={`bi-po-${i.code}`}
                          className="inline-flex items-center gap-1 mt-1 text-[10.5px] mono px-1.5 py-0.5 rounded border border-foreground/15 text-foreground/65 hover:bg-foreground hover:text-background hover:border-foreground transition-colors normal-case"
                          title="Jump to the order this invoice was issued for"
                        >
                          {i.order_code}
                        </button>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <div>{i.seller_name || "—"}</div>
                      {i.autopay_enabled && (
                        <span className="inline-flex items-center gap-1 mt-1 text-[10.5px] rounded px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200">
                          <Repeat className="w-2.5 h-2.5"/>Auto-pay on
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <DueCell invoice={i}/>
                    </td>
                    <td className="px-5 py-4">
                      <InvoiceAmountWithEvents
                        invoiceId={i.id}
                        amount={i.amount}
                        subText={
                          <>
                            {i.status === "paid" && i.paid_via && (
                              <div className="text-[10px] text-foreground/55 mono mt-0.5" data-testid={`bi-paid-via-${i.code}`}>
                                via {i.paid_via}
                              </div>
                            )}
                            {cmTotal > 0 && (
                              <div className="text-[10px] text-emerald-700 mono mt-0.5" data-testid={`bi-cm-${i.code}`}>
                                −{money(cmTotal)} credit memo
                              </div>
                            )}
                            {refunded > 0 && cmTotal === 0 && (
                              <div className="text-[10px] text-foreground/45 mono mt-0.5">
                                −{money(refunded)} refunded
                              </div>
                            )}
                            {balance > 0 && balance !== Number(i.amount) && (
                              <div className="text-[10px] text-foreground/55 mono mt-0.5">
                                {money(balance)} due
                              </div>
                            )}
                          </>
                        }
                      />
                    </td>
                    <td className="px-5 py-4">
                      <StatusChip status={i.status}/>
                    </td>
                    <td className="px-5 py-4 text-right whitespace-nowrap">
                      <div className="flex gap-1.5 justify-end">
                        <Button size="sm" variant="outline" className="rounded-full"
                                onClick={() => setDetailInvoice(i)}
                                data-testid={`bi-view-${i.code}`}>
                          <Eye className="w-3.5 h-3.5 mr-1"/>View
                        </Button>
                        {canPay && (
                          <Button size="sm" className="rounded-full bg-foreground text-background hover:bg-foreground/90"
                                  onClick={() => goPay(i)}
                                  data-testid={`bi-pay-${i.code}`}>
                            <CreditCard className="w-3.5 h-3.5 mr-1"/>Pay
                          </Button>
                        )}
                        {i.status === "paid" && (
                          <Button size="sm" variant="outline" className="rounded-full"
                                  onClick={() => window.open(`/api/invoices/${i.id}/pdf`, "_blank")}
                                  data-testid={`bi-pdf-${i.code}`}>
                            <Download className="w-3.5 h-3.5 mr-1"/>PDF
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {!loading && filtered.length > 0 && (
            <div className="px-5 md:px-6 py-3 border-t border-border flex items-center justify-between flex-wrap gap-3"
                 data-testid="bi-pager">
              <div className="text-[11px] text-foreground/55 mono">
                Showing <strong>{pageStart + 1}</strong>–<strong>{Math.min(pageEnd, filtered.length)}</strong> of <strong>{filtered.length}</strong>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-[11px] text-foreground/55 mono">Rows</label>
                <select
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                  data-testid="bi-page-size"
                  className="h-8 rounded-md border border-border bg-secondary/40 px-2 text-xs"
                >
                  {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
                <Button variant="ghost" size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={safePage <= 1}
                  data-testid="bi-prev"
                  className="h-8 rounded-full px-3">
                  <ChevronLeft className="w-3.5 h-3.5 mr-1"/>Prev
                </Button>
                <div className="text-[11px] mono text-foreground/70 px-1">
                  Page <strong>{safePage}</strong> / {totalPages}
                </div>
                <Button variant="ghost" size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={safePage >= totalPages}
                  data-testid="bi-next"
                  className="h-8 rounded-full px-3">
                  Next<ChevronRight className="w-3.5 h-3.5 ml-1"/>
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Sticky bottom action bar — appears when 1+ payable rows ticked */}
      {selectedIds.size > 0 && (
        <div
          data-testid="bi-bulk-bar"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-30 bg-foreground text-background rounded-2xl shadow-2xl px-5 py-3 flex items-center gap-4 max-w-[92vw]"
        >
          <div className="text-[13px]">
            <span className="font-semibold tabular-nums">{selectedInvoices.length}</span>
            <span className="opacity-70 mx-1">selected ·</span>
            <span className="font-semibold display tabular-nums">{money(selectedTotal)}</span>
            <span className="opacity-60 ml-1 text-[11px] mono uppercase tracking-wider">total</span>
          </div>
          <div className="w-px h-6 bg-background/20"/>
          <button
            type="button"
            onClick={clearSelection}
            data-testid="bi-bulk-clear"
            className="text-[12px] opacity-65 hover:opacity-100"
          >
            Clear
          </button>
          <Button
            size="sm"
            onClick={() => setBulkOpen(true)}
            data-testid="bi-bulk-pay"
            className="rounded-full bg-background text-foreground hover:bg-background/90 font-medium px-4"
          >
            <CreditCard className="w-3.5 h-3.5 mr-1.5"/>
            Pay all selected
          </Button>
        </div>
      )}

      <BuyerInvoiceDetailDialog
        open={!!detailInvoice}
        invoice={detailInvoice}
        onClose={() => setDetailInvoice(null)}
      />

      <BulkPayDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        invoices={selectedInvoices}
        walletBalance={Number(wallet?.balance) || 0}
        onSuccess={() => {
          clearSelection();
          load();
          // Refresh wallet too — balance just dropped.
          api.get("/wallet").then(r => setWallet(r.data || { balance: 0 })).catch(() => {});
        }}
      />
    </>
  );
}
